import jsPDF from "jspdf";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import { server } from "../server";
import axios from "axios";

const DownloadAnnualResult = async ({
  marks,
  heads,
  attendanceData,
  selectedClass,
  selectedSection,
  workingDays,
  holidays = [],   // array of { date: "dd/mm/yyyy", class: [...], occasion: "" }
}) => {
  const subjectOrder = [
    "English",
    "Hindi",
    "Maths",
    "EVS",
    "Science",
    "SST",
    "Sanskrit",
    "GK",
    "AI",
  ];

  const halfYearlyConfig = heads[0].examHeads.find(
    (e) => e.examType.toLowerCase() === "halfyearly",
  );
  const annualConfig = heads[0].examHeads.find(
    (e) => e.examType.toLowerCase() === "annual",
  );

  const halfYearlyHeadLabels = halfYearlyConfig.heads;
  const annualHeadLabels = annualConfig ? annualConfig.heads : [];

  const calcExamMaxTotal = (headLabels) => {
    let total = 0;
    headLabels.forEach((h) => {
      if (h.label.toLowerCase() === "weekly test 1") total += 10;
      else if (h.label.toLowerCase() === "weekly test 2") {
        /* skip */
      } else total += h.max;
    });
    return total;
  };

  const hyMaxTotal  = calcExamMaxTotal(halfYearlyHeadLabels);
  const annMaxTotal = calcExamMaxTotal(annualHeadLabels);

  const getGrade = (percentage) => {
    if (percentage >= 91) return "A1";
    if (percentage >= 81) return "A2";
    if (percentage >= 71) return "B1";
    if (percentage >= 61) return "B2";
    if (percentage >= 51) return "C1";
    if (percentage >= 41) return "C2";
    if (percentage >= 33) return "D";
    return "E";
  };

  const halfYearlyRemarks = halfYearlyConfig.remarks;

  // ── Fetch ALL students in one single API call by class + section ────────
  const studentLookup = {};
  try {
    const bulkRes = await axios.post(
      `${server}/studentdata_using_class_and_section_mongodb`,
      { class: selectedClass, section: selectedSection },
      {
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      },
    );
    const bulkData = bulkRes.data?.["body-json"]?.body?.students || [];
    bulkData.forEach((s) => {
      if (s.admission_no) studentLookup[s.admission_no] = s;
    });
  } catch (error) {
    console.error("Failed to fetch bulk student data:", error);
  }

  // Merge marks with student details
  const enrichedMarks = marks.map((student) => {
    const studentData = studentLookup[student.admission_no];
    return studentData ? { ...student, ...studentData } : student;
  });

  // Sort by rollNo
  enrichedMarks.sort((a, b) => {
    const rollA = Number(a?.studentDetails?.rollNo) || Infinity;
    const rollB = Number(b?.studentDetails?.rollNo) || Infinity;
    return rollA - rollB;
  });

  const pdf = new jsPDF("l", "mm", "a4");

  for (let i = 0; i < enrichedMarks.length; i++) {
    const student = enrichedMarks[i];

    const studentName  = student?.name || "-";
    const fatherName   = student?.parentDetails?.[0]?.name || "-";
    const motherName   = student?.parentDetails?.[1]?.name || "-";
    const currentClass = student?.currentClass || "-";
    const section      = student?.studentDetails?.currentSection || "-";
    const studentImage = student?.studentDetails?.studentImage || "-";
    const rollNo       = student?.studentDetails?.rollNo || "-";
    const dobRaw       = student?.dateOfBirth || "-";
    const dob          = dobRaw !== "-" ? dobRaw.split("-").reverse().join("/") : "-";

    const studentAttendance =
      attendanceData.find((att) => att.admission_no === student.admission_no)
        ?.attendance || [];

    // ── Count present days exactly like AttendanceRegister does ──────────
    // 1. Deduplicate by date
    // 2. Skip any date that is a holiday for this class (holidays in dd/mm/yyyy)
    // 3. Count only "Present" status
    const seenDates = new Set();
    let totalPresentDays = 0;

    studentAttendance.forEach((day) => {
      if (!day.Date) return;
      if (seenDates.has(day.Date)) return;          // deduplicate
      seenDates.add(day.Date);

      // Skip if this date is a holiday for selectedClass — same check as AttendanceRegister
      const isHoliday = holidays.find(
        (h) => h.date === day.Date && h.class.includes(selectedClass),
      );
      if (isHoliday) return;

      if (day.SchoolStatus?.toLowerCase() === "present") totalPresentDays++;
    });

    const attendancePercentage =
      workingDays > 0
        ? ((totalPresentDays / workingDays) * 100).toFixed(2)
        : "0.00";

    const normalize = (str) => str?.trim().toLowerCase();

    const buildExamCells = (headLabels, marksByHead, onObtained, onMax) => {
      let obtained = 0;
      const cells = headLabels
        .map((h) => {
          if (h.label.toLowerCase() === "weekly test 1") {
            const s1 = Number(marksByHead["h1"]) || 0;
            const s2 = Number(marksByHead["h2"]) || 0;
            const nullBoth =
              (marksByHead["h1"] === null || marksByHead["h1"] === 0) &&
              (marksByHead["h2"] === null || marksByHead["h2"] === 0);

            onMax(10);

            if (nullBoth) {
              return `<td class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">AB</p></td>`;
            }
            const combined = Math.round((s1 + s2) / 4);
            obtained += combined;
            onObtained(combined);
            return `<td class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center px-2">${combined}</p></td>`;
          }

          if (h.label.toLowerCase() === "weekly test 2") return null;

          const score = marksByHead[h.id] ?? null;
          onMax(h.max);

          if (score === null || score === 0) {
            return `<td class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">AB</p></td>`;
          }
          obtained += score;
          onObtained(score);
          return `<td class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center px-2">${score}</p></td>`;
        })
        .filter(Boolean)
        .join("");

      return { cellsHtml: cells, obtained };
    };

    const hySubjects  = student.result["half yearly"] || [];
    const annSubjects = student.result["annual"] || [];

    const hySubjectMap  = {};
    const annSubjectMap = {};

    hySubjects.forEach((s)  => { hySubjectMap[normalize(s.subject)]  = s; });
    annSubjects.forEach((s) => { annSubjectMap[normalize(s.subject)] = s; });

    let subjectRowsHtml      = "";
    let subjectCount         = 0;
    let grandHyObtainedNorm  = 0;
    let grandAnnObtainedNorm = 0;

    subjectOrder.forEach((subjectName) => {
      const hyObj  = hySubjectMap[normalize(subjectName)];
      const annObj = annSubjectMap[normalize(subjectName)];

      if (!hyObj && !annObj) return;

      // ── HY cells ──
      let hyObtained = 0, hyMaxSub = 0, hyCells = "";
      if (hyObj) {
        const result = buildExamCells(
          halfYearlyHeadLabels, hyObj.marks || {},
          (v) => { hyObtained += v; },
          (v) => { hyMaxSub  += v; },
        );
        hyCells = result.cellsHtml;
      } else {
        hyCells = halfYearlyHeadLabels
          .filter((h) => h.label.toLowerCase() !== "weekly test 2")
          .map(() => `<td class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">-</p></td>`)
          .join("");
        hyMaxSub = hyMaxTotal;
      }

      const hyGrade = getGrade((hyObtained / (hyMaxSub || hyMaxTotal)) * 100);

      // ── Annual cells ──
      let annObtained = 0, annMaxSub = 0, annCells = "";
      if (annObj) {
        const result = buildExamCells(
          annualHeadLabels, annObj.marks || {},
          (v) => { annObtained += v; },
          (v) => { annMaxSub  += v; },
        );
        annCells = result.cellsHtml;
      } else {
        annCells = annualHeadLabels
          .filter((h) => h.label.toLowerCase() !== "weekly test 2")
          .map(() => `<td class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">-</p></td>`)
          .join("");
        annMaxSub = annMaxTotal;
      }

      const hyNorm  = (hyObtained  / (hyMaxSub  || hyMaxTotal))  * 100;
      const annNorm = (annObtained / (annMaxSub || annMaxTotal)) * 100;

      grandHyObtainedNorm  += hyNorm;
      grandAnnObtainedNorm += annNorm;
      subjectCount++;

      // avgDisplayedPct = rounded percentage shown in the AVG column.
      // Grade is derived from this rounded value so the printed number
      // and the printed grade always agree (no rounding-boundary mismatch).
      // Example: raw avg = 90.5 → avgDisplayedPct = 91 → grade = A1 ✓
      //          raw avg = 90.4 → avgDisplayedPct = 90 → grade = A2 ✓
      const avgPct          = (hyNorm + annNorm) / 2;
      const avgDisplayedPct = Math.round(avgPct);                         // rounded %
      const avgDisplayed    = Math.round((hyObtained + annObtained) / 2); // rounded marks
      const avgGrade        = getGrade(avgDisplayedPct);                  // grade from rounded %

      subjectRowsHtml += `
      <tr>
        <td class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3 text-uppercase">${subjectName.toUpperCase()}</p></td>
        ${hyCells}
        <td class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">${hyObtained}</p></td>
        <td class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">${hyGrade}</p></td>
        ${annCells}
        <td class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">${annObtained}</p></td>
        <td class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">${avgDisplayed}</p></td>
        <td class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">${avgGrade}</p></td>
      </tr>
      `;
    });

    const grandHyMax  = subjectCount * 100;
    const grandAnnMax = subjectCount * 100;

    const grandHyDisplay  = Math.round(grandHyObtainedNorm);
    const grandAnnDisplay = Math.round(grandAnnObtainedNorm);

    const grandHyPct  = grandHyMax  > 0 ? (grandHyObtainedNorm  / grandHyMax)  * 100 : 0;
    const grandAnnPct = grandAnnMax > 0 ? (grandAnnObtainedNorm / grandAnnMax) * 100 : 0;

    const overallAvgPct = (grandHyPct + grandAnnPct) / 2;
    // Same principle for overall: grade derived from rounded % so
    // PERCENTAGE and GRADE printed on the card always match each other.
    const displayPercentage = Math.round(overallAvgPct);
    const overallGrade      = getGrade(displayPercentage);

    const groupedRemarks = halfYearlyRemarks.reduce((acc, r) => {
      if (!acc[r.category]) acc[r.category] = [];
      acc[r.category].push(r);
      return acc;
    }, {});

    let remarksRowsHtml = "";
    for (const [category, items] of Object.entries(groupedRemarks)) {
      remarksRowsHtml += `
        <tr class="bg-warning">
          <th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">${category.toUpperCase()}</p></th>
          <th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">HALF YEARLY</p></th>
          <th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">ANNUAL</p></th>
        </tr>
      `;
      items.forEach((item) => {
        const hyGradeR  = student.remarks?.["half yearly"]?.[item.id] ?? "-";
        const annGradeR = student.remarks?.["annual"]?.[item.id]       ?? "-";
        remarksRowsHtml += `
          <tr>
            <th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">${item.label}</p></th>
            <th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center text-uppercase">${hyGradeR}</p></th>
            <th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center text-uppercase">${annGradeR}</p></th>
          </tr>
        `;
      });
    }

    const buildHeaderCells = (headLabels) =>
      headLabels
        .map((h) => {
          if (h.label.toLowerCase() === "weekly test 1") {
            return `<th class="p-0 py-1"><p class="text-black m-0 fs-6 fw-normal text-center lh-1 fs-8">WEEKLY TEST (10)</p></th>`;
          }
          if (h.label.toLowerCase() === "weekly test 2") return null;
          return `<th class="p-0 py-1"><p class="text-black m-0 fs-6 fw-normal text-center text-uppercase lh-1 fs-8">${h.label} (${h.max})</p></th>`;
        })
        .filter(Boolean)
        .join("");

    const hyHeaderCells  = buildHeaderCells(halfYearlyHeadLabels);
    const annHeaderCells = buildHeaderCells(annualHeadLabels);

    const pageContainer = document.createElement("div");
    pageContainer.style.position = "fixed";
    pageContainer.style.top = "-10000px";
    pageContainer.style.left = "-10000px";
    pageContainer.style.width = "297mm";
    pageContainer.style.height = "210mm";
    pageContainer.style.backgroundColor = "#fff";
    pageContainer.style.display = "flex";
    pageContainer.style.flexDirection = "column";
    pageContainer.style.justifyContent = "space-between";

    pageContainer.innerHTML = `
    <div class="mx-3 my-3">
      <div class="bg-watermark-logo">
        <div class="bg-primary rounded-4 px-3 py-1 mb-2">
          <div class="row align-items-center my-1">
            <div class="col-3 d-flex align-items-center">
              <img src="/assets/images/icon.png" width="70" height="70"/>
              <div class="ms-7">
                <h5 class="m-0 lh-1 text-white text-center">Academic Session</h5>
                <h3 class="text-white fw-bold lh-sm text-center">2025-26</h3>
              </div>
            </div>
            <div class="col-6">
              <h2 class="eczar-font text-center text-white lh-sm m-0">आचार्यकुलम्</h2>
              <p class="fs-4 text-white lh-sm m-0 text-center">Tata Road, Namkum, Ranchi - 834010</p>
              <h3 class="text-white fs-4 lh-sm mb-0 fw-bold text-center text-uppercase">ANNUAL EXAMINATION REPORT CARD</h3>
            </div>
            <div class="col-3 d-flex align-items-end justify-content-end">
              <img src="${studentImage}" class="rounded-4 object-fit-cover" width="75" height="75" crossOrigin="anonymous" />
            </div>
          </div>
        </div>
        
        <div class="px-3 py-2 row">
          <div class="col-3">
            <p class="m-0 fs-5 text-dark lh-sm">Admission No : <span class="fw-bold">${student?.admission_no}</span></p>
            <p class="m-0 fs-5 text-dark lh-sm">Name : <span class="fw-bold">${studentName}</span></p>
          </div>
          <div class="col-4">
            <p class="m-0 fs-5 text-dark lh-sm">Father's Name : <span class="fw-bold">${fatherName}</span></p>
            <p class="m-0 fs-5 text-dark lh-sm">Mother's Name : <span class="fw-bold">${motherName}</span></p>
          </div>
          <div class="col-2">
            <p class="m-0 fs-5 text-dark lh-sm">Class / Section : <span class="fw-bold">${currentClass} / ${section}</span></p>
            <p class="m-0 fs-5 text-dark lh-sm">Roll No : <span class="fw-bold">${rollNo}</span></p>
          </div>
          <div class="col-3">
            <p class="m-0 fs-5 text-dark lh-sm">Date of Birth : <span class="fw-bold">${dob}</span></p>
            <p class="m-0 fs-5 text-dark lh-sm">Attendance : <span class="fw-bold">${totalPresentDays} / ${workingDays} (${attendancePercentage}%)</span></p>
          </div>
        </div>

        <div class="px-3 pt-2 pb-1 row">
          <div class="col-12">
            <table class="table result-table mb-2">
              <thead class="bg-warning">
                <tr>
                  <th class="p-0 py-1"><p class="text-black m-0 fs-9 fw-normal text-center lh-1">SCHOLASTIC AREAS</p></th>
                  <th class="p-0 py-1" colspan="${halfYearlyHeadLabels.filter((h) => h.label.toLowerCase() !== "weekly test 2").length + 2}">
                    <p class="text-black m-0 fs-6 fw-normal text-center">HALF YEARLY EXAMINATION &mdash; ${hyMaxTotal} MARKS</p>
                  </th>
                  <th class="p-0 py-1" colspan="${annualHeadLabels.filter((h) => h.label.toLowerCase() !== "weekly test 2").length + 3}">
                    <p class="text-black m-0 fs-6 fw-normal text-center">ANNUAL EXAMINATION &mdash; ${annMaxTotal} MARKS</p>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th class="p-0 py-1"><p class="text-black m-0 fs-6 fw-normal ms-3">SUBJECTS</p></th>
                  ${hyHeaderCells}
                  <th class="p-0 py-1"><p class="text-black m-0 fs-6 fw-normal text-center lh-1 fs-8 px-2">MARKS OBTAINED (${hyMaxTotal})</p></th>
                  <th class="p-0 py-1"><p class="text-black m-0 fs-6 fw-normal text-center lh-1 fs-8 px-2">GRADE</p></th>
                  ${annHeaderCells}
                  <th class="p-0 py-1"><p class="text-black m-0 fs-6 fw-normal text-center lh-1 fs-8 px-2">MARKS OBTAINED (${annMaxTotal})</p></th>
                  <th class="p-0 py-1"><p class="text-black m-0 fs-6 fw-normal text-center lh-1 fs-8 px-2">AVG. OF HY + ANNUAL</p></th>
                  <th class="p-0 py-1"><p class="text-black m-0 fs-6 fw-normal text-center lh-1 fs-8 px-2">GRADE</p></th>
                </tr>
                ${subjectRowsHtml}
              </tbody>
            </table>
          </div>
        </div>
        
        <div class="px-3 pb-2 row g-3">
          <div class="col-3">
            <div class="bg-warning border border-1 border-dark">
              <p class="text-black m-0 fs-6 text-center">HY TOTAL &mdash; ${grandHyDisplay}/${grandHyMax}</p>
            </div>
          </div>
          <div class="col-3">
            <div class="bg-warning border border-1 border-dark">
              <p class="text-black m-0 fs-6 text-center">ANNUAL TOTAL &mdash; ${grandAnnDisplay}/${grandAnnMax}</p>
            </div>
          </div>
          <div class="col-3">
            <div class="bg-warning border border-1 border-dark">
              <p class="text-black m-0 fs-6 text-center">PERCENTAGE &mdash; ${displayPercentage}%</p>
            </div>
          </div>
          <div class="col-3">
            <div class="bg-warning border border-1 border-dark">
              <p class="text-black m-0 fs-6 text-center">GRADE &mdash; ${overallGrade}</p>
            </div>
          </div>
        </div>

        <div class="px-3 pt-1 row g-3 pb-0 d-flex align-items-stretch">
          <div class="col-6 d-flex flex-column">
            <table class="table result-table">
              <tbody>
                ${remarksRowsHtml}
              </tbody>
            </table>
          </div>

          <div class="col-6 d-flex flex-column justify-content-between">
            <div>
              <div class="bg-warning border border-1 border-dark">
                <p class="text-black m-0 fs-6 text-center">CLASS TEACHER'S REMARKS</p>
              </div>
              <div class="border border-1 border-dark p-4 text-danger lh-sm">
                ${student?.teacherRemarks?.["annual"] || "-"}
              </div>
            </div>

            <table class="table result-table">
              <thead class="bg-warning">
                <tr>
                  <th class="p-0" colspan="2"><p class="text-black m-0 fs-6 fw-normal text-center">GRADING SCALE FOR SCHOLASTIC AREAS</p></th>
                </tr>
              </thead>
              <tbody>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">91-100</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">A1</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">81-90</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">A2</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">71-80</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">B1</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">61-70</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">B2</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">51-60</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">C1</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">41-50</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">C2</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">33-40</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">D</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">32 & Below</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">E</p></th></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="px-3 row mt-n2">
          <div class="col-6 d-flex justify-content-end flex-column">
            <p class="text-black m-0 fs-6 fw-bold text-center">CLASS TEACHER</p>
          </div>
          <div class="col-6 text-center">
            <img src="/assets/images/principal.png" width="50" crossOrigin="anonymous" class="mb-principal-sign" />
            <p class="text-black m-0 fs-6 fw-bold text-center">PRINCIPAL</p>
          </div>
        </div>
      </div>
    </div>
  `;

    document.body.appendChild(pageContainer);

    const canvas = await html2canvas(pageContainer, {
      scale: 1.5,
      useCORS: true,
    });
    const imgData = canvas.toDataURL("image/png");

    if (i > 0) pdf.addPage();
    pdf.addImage(imgData, "PNG", 0, 0, 297, 210);

    pageContainer.remove();
  }

  const pdfBlob = pdf.output("blob");
  const sessionYear = "2025";
  const fileName = `${selectedClass}-${selectedSection}-Annual-ReportCard-${sessionYear}.pdf`;

  saveAs(pdfBlob, fileName);
};

export default DownloadAnnualResult;